import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft, ArrowRight, Save, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerController } from '@/components/ui/date-picker';
import { toast } from 'sonner';
import DriverWizardStepper from '@/components/drivers/DriverWizardStepper';
import DocumentUploadField from '@/components/drivers/DocumentUploadField';
import {
    useCreateDriver,
    useUploadProfilePhoto,
    useUploadKycDocuments,
    useUploadVehicleDetails,
    useUploadBankingInfo,
    useUpdateDriver,
    useDriver
} from '@/api-services/hooks/useDriver';
import { adminApiClient } from '@/api-services/hooks/adminApiClient';
import { DriverDTO } from '@/api-services/hooks/adminTypes';
import { ApiResponse, PagedData } from '@/api-services/hooks/types';

// Step 1: Personal Details Schema
const personalSchema = z.object({
    fullName: z.string().min(2, 'Full name is required'),
    email: z.string().email('Invalid email address').optional().or(z.literal('')),
    phoneNumber: z.string().min(10, 'Phone number must be at least 10 digits'),
    dateOfBirth: z.string().optional(),
    gender: z.string().optional(),
    address: z.string().optional(),
    emergencyContactNumber: z.string().optional(),
    preferredLanguage: z.string().optional(),
});

// Step 2: Profile Photo Schema (Client-side validation only for file presence)
const profilePhotoSchema = z.object({});

// Step 3: KYC Documents Schema
const kycSchema = z.object({
    govtIdType: z.string().min(1, 'ID Type is required'),
    govtIdNumber: z.string().min(1, 'ID Number is required'),
});

// Step 4: Vehicle Details Schema
const vehicleSchema = z.object({
    vehicleType: z.string().min(1, 'Vehicle Type is required'),
    vehicleCategoryId: z.string().optional(), // Will be converted to number
    vehicleMake: z.string().min(1, 'Make is required'),
    vehicleModel: z.string().min(1, 'Model is required'),
    vehicleRegistrationNumber: z.string().min(1, 'Registration Number is required'),
    yearOfManufacture: z.string().optional(), // Will be converted to number
    vehicleColor: z.string().optional(),
    seatingCapacity: z.string().optional(), // Will be converted to number
    luggageCapacity: z.string().optional(), // Will be converted to number
});

// Step 5: Banking Schema
const bankingSchema = z.object({
    bankAccountHolderName: z.string().min(1, 'Account Holder Name is required'),
    bankName: z.string().min(1, 'Bank Name is required'),
    accountNumber: z.string().min(1, 'Account Number is required'),
    ifscCode: z.string().min(1, 'IFSC Code is required'),
    swiftCode: z.string().optional(),
    paymentFrequency: z.string().optional(),
});

const CreateDriverWizardPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const currentStep = searchParams.get('step') || 'personal';
    const driverIdParam = searchParams.get('driverId');
    const driverId = driverIdParam ? parseInt(driverIdParam) : null;

    // State for file uploads
    const [profilePhoto, setProfilePhoto] = useState<File | string | null>(null);
    const [kycFiles, setKycFiles] = useState<{
        govtIdFront: File | string | null;
        govtIdBack: File | string | null;
        licenseFront: File | string | null;
        licenseBack: File | string | null;
        selfie: File | string | null;
    }>({
        govtIdFront: null,
        govtIdBack: null,
        licenseFront: null,
        licenseBack: null,
        selfie: null,
    });
    const [vehicleFiles, setVehicleFiles] = useState<{
        ownership: File | string | null;
        insurance: File | string | null;
        roadTax: File | string | null;
    }>({
        ownership: null,
        insurance: null,
        roadTax: null,
    });

    // Completed steps tracking
    const [completedSteps, setCompletedSteps] = useState<string[]>([]);

    // Deferred Creation State
    const [formData, setFormData] = useState<{
        personal: any;
        kyc: any;
        vehicle: any;
        banking: any;
    }>({
        personal: null,
        kyc: null,
        vehicle: null,
        banking: null
    });

    // API Hooks
    const { mutateAsync: createDriver, isPending: isCreating } = useCreateDriver();
    const { mutateAsync: uploadPhoto, isPending: isUploadingPhoto } = useUploadProfilePhoto();
    const { mutateAsync: uploadKyc, isPending: isUploadingKyc } = useUploadKycDocuments();
    const { mutateAsync: uploadVehicle, isPending: isUploadingVehicle } = useUploadVehicleDetails();
    const { mutateAsync: uploadBanking, isPending: isUploadingBanking } = useUploadBankingInfo();
    const { mutateAsync: updateDriver, isPending: isUpdating } = useUpdateDriver();

    // Fetch driver data for Edit Mode
    const { data: driverData, isLoading: isLoadingDriver } = useDriver(driverId || 0, {
        enabled: !!driverId
    });

    // Forms
    const personalForm = useForm({ resolver: zodResolver(personalSchema) });
    const kycForm = useForm({ resolver: zodResolver(kycSchema) });
    const vehicleForm = useForm({ resolver: zodResolver(vehicleSchema) });
    const bankingForm = useForm({ resolver: zodResolver(bankingSchema) });

    // Populate forms when driver data is loaded
    useEffect(() => {
        if (driverData) {
            // Personal Details
            personalForm.reset({
                fullName: driverData.fullName,
                email: driverData.email,
                phoneNumber: driverData.phoneNumber,
                dateOfBirth: driverData.dateOfBirth,
                gender: driverData.gender,
                address: driverData.address,
                emergencyContactNumber: driverData.emergencyContactNumber,
                preferredLanguage: driverData.preferredLanguage,
            });

            // Profile Photo
            if (driverData.profilePhotoUrl) {
                setProfilePhoto(driverData.profilePhotoUrl);
            }

            // KYC Documents
            kycForm.reset({
                govtIdType: driverData.govtIdType,
                govtIdNumber: driverData.govtIdNumber,
            });
            setKycFiles({
                govtIdFront: driverData.govtIdFrontUrl || null,
                govtIdBack: driverData.govtIdBackUrl || null,
                licenseFront: driverData.drivingLicenseFrontUrl || null,
                licenseBack: driverData.drivingLicenseBackUrl || null,
                selfie: driverData.selfieWithIdUrl || null,
            });

            // Vehicle Details
            vehicleForm.reset({
                vehicleType: driverData.vehicleType,
                vehicleCategoryId: driverData.vehicleCategoryId?.toString(),
                vehicleMake: driverData.vehicleMake,
                vehicleModel: driverData.vehicleModel,
                vehicleRegistrationNumber: driverData.vehicleRegistrationNumber,
                yearOfManufacture: driverData.yearOfManufacture?.toString(),
                vehicleColor: driverData.vehicleColor,
                seatingCapacity: driverData.seatingCapacity?.toString(),
                luggageCapacity: driverData.luggageCapacity?.toString(),
            });
            setVehicleFiles({
                ownership: driverData.ownershipDocUrl || null,
                insurance: driverData.vehicleInsuranceCertUrl || null,
                roadTax: driverData.roadTaxDocUrl || null,
            });

            // Banking Details
            bankingForm.reset({
                bankAccountHolderName: driverData.bankAccountHolderName,
                bankName: driverData.bankName,
                accountNumber: driverData.accountNumber,
                ifscCode: driverData.ifscCode,
                swiftCode: driverData.swiftCode,
                paymentFrequency: driverData.paymentFrequency,
            });

            // Mark all steps as completed if we have data
            if (!completedSteps.includes('review')) {
                setCompletedSteps(['personal', 'photo', 'kyc', 'vehicle', 'bank', 'review']);
            }
        }
    }, [driverData, personalForm, kycForm, vehicleForm, bankingForm]);

    // Step Definitions
    const steps = [
        { id: 'personal', label: 'Personal Details', description: 'Basic info' },
        { id: 'photo', label: 'Profile Photo', description: 'Upload photo' },
        { id: 'kyc', label: 'KYC Documents', description: 'Identity verification' },
        { id: 'vehicle', label: 'Vehicle', description: 'Car details' },
        { id: 'bank', label: 'Banking', description: 'Payout info' },
        { id: 'review', label: 'Review', description: 'Complete' },
    ];

    // Handlers
    const handleStepClick = (stepId: string) => {
        if (driverId) {
            setSearchParams({ step: stepId, driverId: driverId.toString() });
        } else if (stepId === 'personal') {
            setSearchParams({ step: stepId });
        }
    };

    const nextStep = (nextStepId: string) => {
        if (!completedSteps.includes(currentStep)) {
            setCompletedSteps([...completedSteps, currentStep]);
        }
        setSearchParams({ step: nextStepId, driverId: driverId?.toString() || '' });
        window.scrollTo(0, 0);
    };

    // Step 1: Personal Details Submit
    const onPersonalSubmit = (data: any) => {
        if (driverId) {
            // Edit Mode: Update existing driver immediately
            updateDriver({ id: driverId, data })
                .then(() => {
                    toast.success('Personal details updated');
                    setSearchParams({ step: 'photo', driverId: driverId.toString() });
                    if (!completedSteps.includes('personal')) {
                        setCompletedSteps([...completedSteps, 'personal']);
                    }
                })
                .catch((error) => {
                    toast.error(`Failed to update driver: ${error.message}`);
                });
        } else {
            // Create Mode: Save to state and proceed
            setFormData(prev => ({ ...prev, personal: data }));
            setSearchParams({ step: 'photo' });
            if (!completedSteps.includes('personal')) {
                setCompletedSteps([...completedSteps, 'personal']);
            }
        }
    };

    // Step 2: Profile Photo Submit
    const onPhotoSubmit = () => {
        if (driverId) {
            // Edit Mode: Upload immediately
            if (typeof profilePhoto === 'string') {
                nextStep('kyc');
                return;
            }

            if (!profilePhoto) {
                nextStep('kyc');
                return;
            }

            uploadPhoto({ driverId, file: profilePhoto })
                .then(() => {
                    toast.success('Profile photo uploaded');
                    nextStep('kyc');
                })
                .catch((error) => {
                    toast.error(`Upload failed: ${error.message}`);
                });
        } else {
            // Create Mode: Proceed (photo is already in state)
            nextStep('kyc');
        }
    };

    // Step 3: KYC Submit
    const onKycSubmit = (data: any) => {
        // Validate files
        if (!kycFiles.govtIdFront || !kycFiles.govtIdBack || !kycFiles.licenseFront || !kycFiles.licenseBack || !kycFiles.selfie) {
            toast.error('Please upload all required KYC documents');
            return;
        }

        if (driverId) {
            // Edit Mode: Upload immediately
            // Prepare files for upload - only include new Files
            const filesToUpload: any = {};
            if (kycFiles.govtIdFront instanceof File) filesToUpload.govtIdFrontUrl = kycFiles.govtIdFront;
            if (kycFiles.govtIdBack instanceof File) filesToUpload.govtIdBackUrl = kycFiles.govtIdBack;
            if (kycFiles.licenseFront instanceof File) filesToUpload.drivingLicenseFrontUrl = kycFiles.licenseFront;
            if (kycFiles.licenseBack instanceof File) filesToUpload.drivingLicenseBackUrl = kycFiles.licenseBack;
            if (kycFiles.selfie instanceof File) filesToUpload.selfieWithIdUrl = kycFiles.selfie;

            // If no new files are being uploaded, just proceed to the next step
            if (Object.keys(filesToUpload).length === 0) {
                nextStep('vehicle');
                return;
            }

            uploadKyc({
                driverId,
                govtIdType: data.govtIdType,
                govtIdNumber: data.govtIdNumber,
                files: filesToUpload,
            })
                .then(() => {
                    toast.success('KYC documents uploaded');
                    nextStep('vehicle');
                })
                .catch((error) => {
                    toast.error(`Upload failed: ${error.message}`);
                });
        } else {
            // Create Mode: Save to state and proceed
            setFormData(prev => ({ ...prev, kyc: data }));
            nextStep('vehicle');
        }
    };

    // Step 4: Vehicle Submit
    const onVehicleSubmit = (data: any) => {
        // Validate files
        if (!vehicleFiles.ownership || !vehicleFiles.insurance || !vehicleFiles.roadTax) {
            toast.error('Please upload all required vehicle documents');
            return;
        }

        if (driverId) {
            // Edit Mode: Upload immediately
            const filesToUpload: any = {};
            if (vehicleFiles.ownership instanceof File) filesToUpload.ownershipDocUrl = vehicleFiles.ownership;
            if (vehicleFiles.insurance instanceof File) filesToUpload.insuranceDocUrl = vehicleFiles.insurance;
            if (vehicleFiles.roadTax instanceof File) filesToUpload.roadTaxDocUrl = vehicleFiles.roadTax;

            uploadVehicle({
                driverId,
                vehicleType: data.vehicleType,
                vehicleCategoryId: data.vehicleCategoryId ? parseInt(data.vehicleCategoryId) : 0,
                vehicleMake: data.vehicleMake,
                vehicleModel: data.vehicleModel,
                vehicleRegistrationNumber: data.vehicleRegistrationNumber,
                yearOfManufacture: data.yearOfManufacture ? parseInt(data.yearOfManufacture) : 0,
                vehicleColor: data.vehicleColor || '',
                seatingCapacity: data.seatingCapacity ? parseInt(data.seatingCapacity) : 4,
                luggageCapacity: data.luggageCapacity ? parseInt(data.luggageCapacity) : 0,
                files: filesToUpload,
            })
                .then(() => {
                    toast.success('Vehicle details saved');
                    nextStep('bank');
                })
                .catch((error) => {
                    toast.error(`Failed to save vehicle details: ${error.message}`);
                });
        } else {
            // Create Mode: Save to state and proceed
            setFormData(prev => ({ ...prev, vehicle: data }));
            nextStep('bank');
        }
    };

    // Step 5: Banking Submit
    const onBankingSubmit = (data: any) => {
        if (driverId) {
            // Edit Mode: Upload immediately
            uploadBanking({
                driverId,
                ...data,
                eWalletId: '', // Optional
            })
                .then(() => {
                    toast.success('Banking details saved');
                    nextStep('review');
                })
                .catch((error) => {
                    toast.error(`Failed to save banking details: ${error.message}`);
                });
        } else {
            // Create Mode: Save to state and proceed
            setFormData(prev => ({ ...prev, banking: data }));
            nextStep('review');
        }
    };

    // Step 6: Final Completion
    const onComplete = async () => {
        if (driverId) {
            // Edit Mode: Just finish
            toast.success('Driver details updated!');
            navigate('/admin/drivers');
            return;
        }

        // Create Mode: Execute Chain
        try {
            // 1. Create Driver
            const createResponse: any = await createDriver(formData.personal);
            
            console.log('Create driver response:', createResponse);
            
            // Try multiple paths to extract the driver ID
            let newDriverId = 
                createResponse?.data?.driverId || 
                createResponse?.driverId || 
                createResponse?.data?.data?.driverId ||
                createResponse?.id ||
                createResponse?.data?.id;

            // Extract ID from message string if present (e.g., "Driver (52) Saved Successfully.")
            if (!newDriverId && createResponse?.message) {
                const messageMatch = createResponse.message.match(/Driver \((\d+)\)/);
                if (messageMatch && messageMatch[1]) {
                    newDriverId = parseInt(messageMatch[1], 10);
                    console.log('Extracted driver ID from message:', newDriverId);
                }
            }

            console.log('Extracted driver ID:', newDriverId);

            // Fallback: Fetch the most recently created driver by phone number
            if (!newDriverId && formData.personal.phoneNumber) {
                console.log('Attempting to recover driver ID using phone number:', formData.personal.phoneNumber);
                try {
                    // Wait a bit for the database to be updated
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    
                    const listResponse = await adminApiClient.get<any>(
                        '/drivers/list',
                        { page: 0, size: 10 }
                    );
                    console.log('List response for recovery:', listResponse);
                    
                    const driversList = listResponse.data?.content || listResponse.content || (Array.isArray(listResponse.data) ? listResponse.data : []);
                    
                    // Find by phone number, prioritizing most recent
                    const foundDriver = driversList
                        .filter((d: DriverDTO) => d.phoneNumber === formData.personal.phoneNumber)
                        .sort((a: DriverDTO, b: DriverDTO) => (b.driverId || 0) - (a.driverId || 0))[0];
                    
                    if (foundDriver?.driverId) {
                        newDriverId = foundDriver.driverId;
                        console.log('Successfully recovered driver ID:', newDriverId);
                    }
                } catch (err) {
                    console.error('Failed to recover driver ID:', err);
                }
            }

            if (!newDriverId) {
                toast.error('Driver created but failed to retrieve ID. Please check the list.');
                navigate('/admin/drivers');
                return;
            }

            toast.success(`Driver created successfully! ID: ${newDriverId}`);

            // 2. Upload Profile Photo
            if (profilePhoto && profilePhoto instanceof File) {
                await uploadPhoto({ driverId: newDriverId, file: profilePhoto });
            }

            // 3. Upload KYC
            if (formData.kyc && kycFiles) {
                await uploadKyc({
                    driverId: newDriverId,
                    govtIdType: formData.kyc.govtIdType,
                    govtIdNumber: formData.kyc.govtIdNumber,
                    files: {
                        govtIdFrontUrl: kycFiles.govtIdFront as File,
                        govtIdBackUrl: kycFiles.govtIdBack as File,
                        drivingLicenseFrontUrl: kycFiles.licenseFront as File,
                        drivingLicenseBackUrl: kycFiles.licenseBack as File,
                        selfieWithIdUrl: kycFiles.selfie as File,
                    },
                });
            }

            // 4. Upload Vehicle
            if (formData.vehicle && vehicleFiles) {
                await uploadVehicle({
                    driverId: newDriverId,
                    vehicleType: formData.vehicle.vehicleType,
                    vehicleCategoryId: formData.vehicle.vehicleCategoryId ? parseInt(formData.vehicle.vehicleCategoryId) : 0,
                    vehicleMake: formData.vehicle.vehicleMake,
                    vehicleModel: formData.vehicle.vehicleModel,
                    vehicleRegistrationNumber: formData.vehicle.vehicleRegistrationNumber,
                    yearOfManufacture: formData.vehicle.yearOfManufacture ? parseInt(formData.vehicle.yearOfManufacture) : 0,
                    vehicleColor: formData.vehicle.vehicleColor || '',
                    seatingCapacity: formData.vehicle.seatingCapacity ? parseInt(formData.vehicle.seatingCapacity) : 4,
                    luggageCapacity: formData.vehicle.luggageCapacity ? parseInt(formData.vehicle.luggageCapacity) : 0,
                    files: {
                        ownershipDocUrl: vehicleFiles.ownership as File,
                        insuranceDocUrl: vehicleFiles.insurance as File,
                        roadTaxDocUrl: vehicleFiles.roadTax as File,
                    },
                });
            }

            // 5. Upload Banking
            if (formData.banking) {
                await uploadBanking({
                    driverId: newDriverId,
                    ...formData.banking,
                    eWalletId: '',
                });
            }

            toast.success('Driver onboarding completed successfully!');
            navigate('/admin/drivers');

        } catch (error: any) {
            console.error('Onboarding chain failed:', error);
            toast.error(`Onboarding failed: ${error.message || 'Unknown error'}`);
            // We might want to stay on the page or redirect to list
            // If partial success, the driver exists.
        }
    };

    const isLoading = isCreating || isUploadingPhoto || isUploadingKyc || isUploadingVehicle || isUploadingBanking || isUpdating;

    return (
        <div className="space-y-6 animate-fade-in max-w-5xl mx-auto pb-10">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/admin/drivers')}
                        className="hover:bg-muted"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Drivers
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold text-foreground">Add New Driver</h1>
                        <p className="text-muted-foreground mt-1">Complete the wizard to register a new driver</p>
                    </div>
                </div>
            </div>

            {/* Stepper */}
            <Card>
                <CardContent className="pt-6">
                    <DriverWizardStepper
                        steps={steps}
                        currentStep={currentStep}
                        completedSteps={completedSteps}
                        onStepClick={handleStepClick}
                        allowNavigation={!!driverId}
                    />
                </CardContent>
            </Card>

            {/* Step Content */}
            <Card className="min-h-[400px]">
                <CardHeader>
                    <CardTitle>{steps.find(s => s.id === currentStep)?.label}</CardTitle>
                    <CardDescription>{steps.find(s => s.id === currentStep)?.description}</CardDescription>
                </CardHeader>
                <CardContent>

                    {/* Step 1: Personal Details */}
                    {currentStep === 'personal' && (
                        <form id="personal-form" onSubmit={personalForm.handleSubmit(onPersonalSubmit)} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="fullName">Full Name *</Label>
                                    <Input {...personalForm.register('fullName')} placeholder="John Doe" />
                                    {personalForm.formState.errors.fullName && (
                                        <p className="text-sm text-destructive">{personalForm.formState.errors.fullName.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phoneNumber">Phone Number *</Label>
                                    <Input {...personalForm.register('phoneNumber')} placeholder="+1234567890" />
                                    {personalForm.formState.errors.phoneNumber && (
                                        <p className="text-sm text-destructive">{personalForm.formState.errors.phoneNumber.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input {...personalForm.register('email')} placeholder="john@example.com" />
                                    {personalForm.formState.errors.email && (
                                        <p className="text-sm text-destructive">{personalForm.formState.errors.email.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <DatePickerController
                                        control={personalForm.control}
                                        name="dateOfBirth"
                                        label="Date of Birth"
                                        placeholder="Select date of birth"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="gender">Gender</Label>
                                    <Select onValueChange={(val) => personalForm.setValue('gender', val)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select gender" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="MALE">Male</SelectItem>
                                            <SelectItem value="FEMALE">Female</SelectItem>
                                            <SelectItem value="OTHER">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="preferredLanguage">Preferred Language</Label>
                                    <Select onValueChange={(val) => personalForm.setValue('preferredLanguage', val)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select language" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="ENGLISH">English</SelectItem>
                                            <SelectItem value="SPANISH">Spanish</SelectItem>
                                            <SelectItem value="FRENCH">French</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="address">Address</Label>
                                    <Input {...personalForm.register('address')} placeholder="123 Main St" />
                                </div>
                            </div>
                        </form>
                    )}

                    {/* Step 2: Profile Photo */}
                    {currentStep === 'photo' && (
                        <div className="space-y-6 max-w-md mx-auto text-center">
                            <DocumentUploadField
                                label="Profile Photo"
                                value={profilePhoto || undefined}
                                onChange={setProfilePhoto}
                                accept="image/*"
                                placeholder="Upload a clear face photo"
                            />
                        </div>
                    )}

                    {/* Step 3: KYC Documents */}
                    {currentStep === 'kyc' && (
                        <form id="kyc-form" onSubmit={kycForm.handleSubmit(onKycSubmit)} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="govtIdType">Government ID Type *</Label>
                                    <Select onValueChange={(val) => kycForm.setValue('govtIdType', val)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select ID type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="NATIONAL_ID">National ID</SelectItem>
                                            <SelectItem value="PASSPORT">Passport</SelectItem>
                                            <SelectItem value="DRIVING_LICENSE">Driving License</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {kycForm.formState.errors.govtIdType && (
                                        <p className="text-sm text-destructive">{kycForm.formState.errors.govtIdType.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="govtIdNumber">ID Number *</Label>
                                    <Input {...kycForm.register('govtIdNumber')} placeholder="Enter ID number" />
                                    {kycForm.formState.errors.govtIdNumber && (
                                        <p className="text-sm text-destructive">{kycForm.formState.errors.govtIdNumber.message as string}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <DocumentUploadField
                                    label="Government ID Front *"
                                    value={kycFiles.govtIdFront || undefined}
                                    onChange={(f) => setKycFiles(prev => ({ ...prev, govtIdFront: f }))}
                                    required
                                />
                                <DocumentUploadField
                                    label="Government ID Back *"
                                    value={kycFiles.govtIdBack || undefined}
                                    onChange={(f) => setKycFiles(prev => ({ ...prev, govtIdBack: f }))}
                                    required
                                />
                                <DocumentUploadField
                                    label="Driving License Front *"
                                    value={kycFiles.licenseFront || undefined}
                                    onChange={(f) => setKycFiles(prev => ({ ...prev, licenseFront: f }))}
                                    required
                                />
                                <DocumentUploadField
                                    label="Driving License Back *"
                                    value={kycFiles.licenseBack || undefined}
                                    onChange={(f) => setKycFiles(prev => ({ ...prev, licenseBack: f }))}
                                    required
                                />
                                <DocumentUploadField
                                    label="Selfie with ID *"
                                    value={kycFiles.selfie || undefined}
                                    onChange={(f) => setKycFiles(prev => ({ ...prev, selfie: f }))}
                                    required
                                />
                            </div>
                        </form>
                    )}

                    {/* Step 4: Vehicle Details */}
                    {currentStep === 'vehicle' && (
                        <form id="vehicle-form" onSubmit={vehicleForm.handleSubmit(onVehicleSubmit)} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="vehicleType">Vehicle Type *</Label>
                                    <Select onValueChange={(val) => vehicleForm.setValue('vehicleType', val)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="CAR">Car</SelectItem>
                                            <SelectItem value="BIKE">Bike</SelectItem>
                                            <SelectItem value="AUTO">Auto</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {vehicleForm.formState.errors.vehicleType && (
                                        <p className="text-sm text-destructive">{vehicleForm.formState.errors.vehicleType.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="vehicleMake">Make *</Label>
                                    <Input {...vehicleForm.register('vehicleMake')} placeholder="Toyota" />
                                    {vehicleForm.formState.errors.vehicleMake && (
                                        <p className="text-sm text-destructive">{vehicleForm.formState.errors.vehicleMake.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="vehicleModel">Model *</Label>
                                    <Input {...vehicleForm.register('vehicleModel')} placeholder="Corolla" />
                                    {vehicleForm.formState.errors.vehicleModel && (
                                        <p className="text-sm text-destructive">{vehicleForm.formState.errors.vehicleModel.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="vehicleRegistrationNumber">Registration Number *</Label>
                                    <Input {...vehicleForm.register('vehicleRegistrationNumber')} placeholder="ABC-1234" />
                                    {vehicleForm.formState.errors.vehicleRegistrationNumber && (
                                        <p className="text-sm text-destructive">{vehicleForm.formState.errors.vehicleRegistrationNumber.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="yearOfManufacture">Year</Label>
                                    <Input type="number" {...vehicleForm.register('yearOfManufacture')} placeholder="2020" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="vehicleColor">Color</Label>
                                    <Input {...vehicleForm.register('vehicleColor')} placeholder="White" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <DocumentUploadField
                                    label="Ownership Document *"
                                    value={vehicleFiles.ownership || undefined}
                                    onChange={(f) => setVehicleFiles(prev => ({ ...prev, ownership: f }))}
                                    required
                                />
                                <DocumentUploadField
                                    label="Insurance Document *"
                                    value={vehicleFiles.insurance || undefined}
                                    onChange={(f) => setVehicleFiles(prev => ({ ...prev, insurance: f }))}
                                    required
                                />
                                <DocumentUploadField
                                    label="Road Tax Document *"
                                    value={vehicleFiles.roadTax || undefined}
                                    onChange={(f) => setVehicleFiles(prev => ({ ...prev, roadTax: f }))}
                                    required
                                />
                            </div>
                        </form>
                    )}

                    {/* Step 5: Banking */}
                    {currentStep === 'bank' && (
                        <form id="banking-form" onSubmit={bankingForm.handleSubmit(onBankingSubmit)} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="bankAccountHolderName">Account Holder Name *</Label>
                                    <Input {...bankingForm.register('bankAccountHolderName')} placeholder="John Doe" />
                                    {bankingForm.formState.errors.bankAccountHolderName && (
                                        <p className="text-sm text-destructive">{bankingForm.formState.errors.bankAccountHolderName.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="bankName">Bank Name *</Label>
                                    <Input {...bankingForm.register('bankName')} placeholder="Chase Bank" />
                                    {bankingForm.formState.errors.bankName && (
                                        <p className="text-sm text-destructive">{bankingForm.formState.errors.bankName.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="accountNumber">Account Number *</Label>
                                    <Input {...bankingForm.register('accountNumber')} placeholder="1234567890" />
                                    {bankingForm.formState.errors.accountNumber && (
                                        <p className="text-sm text-destructive">{bankingForm.formState.errors.accountNumber.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="ifscCode">IFSC / Routing Code *</Label>
                                    <Input {...bankingForm.register('ifscCode')} placeholder="ABCD0123456" />
                                    {bankingForm.formState.errors.ifscCode && (
                                        <p className="text-sm text-destructive">{bankingForm.formState.errors.ifscCode.message as string}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="swiftCode">SWIFT Code</Label>
                                    <Input {...bankingForm.register('swiftCode')} placeholder="ABCDUS33" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="paymentFrequency">Payment Frequency</Label>
                                    <Select onValueChange={(val) => bankingForm.setValue('paymentFrequency', val)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select frequency" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="WEEKLY">Weekly</SelectItem>
                                            <SelectItem value="BIWEEKLY">Bi-Weekly</SelectItem>
                                            <SelectItem value="MONTHLY">Monthly</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </form>
                    )}

                    {/* Step 6: Review */}
                    {currentStep === 'review' && (
                        <div className="text-center py-10">
                            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-success" />
                            </div>
                            <h2 className="text-2xl font-bold mb-2">Ready to Complete</h2>
                            <p className="text-muted-foreground mb-8">
                                All information has been entered. Click below to complete the onboarding process.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-base">Personal Details</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-sm text-muted-foreground">Name: {personalForm.getValues('fullName')}</p>
                                        <p className="text-sm text-muted-foreground">Phone: {personalForm.getValues('phoneNumber')}</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-base">Vehicle</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-sm text-muted-foreground">Type: {vehicleForm.getValues('vehicleType')}</p>
                                        <p className="text-sm text-muted-foreground">Reg: {vehicleForm.getValues('vehicleRegistrationNumber')}</p>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    )}

                </CardContent>
                <CardFooter className="flex justify-between border-t pt-6">
                    <Button
                        variant="outline"
                        onClick={() => {
                            // Handle back navigation
                            const currentIndex = steps.findIndex(s => s.id === currentStep);
                            if (currentIndex > 0) {
                                setSearchParams({ step: steps[currentIndex - 1].id, driverId: driverId?.toString() || '' });
                            } else {
                                navigate('/admin/drivers');
                            }
                        }}
                    >
                        Back
                    </Button>

                    {currentStep === 'personal' && (
                        <Button type="submit" form="personal-form" disabled={isLoading}>
                            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Save & Next
                        </Button>
                    )}

                    {currentStep === 'photo' && (
                        <Button onClick={onPhotoSubmit} disabled={isLoading}>
                            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {profilePhoto ? 'Upload & Next' : 'Skip & Next'}
                        </Button>
                    )}

                    {currentStep === 'kyc' && (
                        <Button type="submit" form="kyc-form" disabled={isLoading}>
                            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Upload & Next
                        </Button>
                    )}

                    {currentStep === 'vehicle' && (
                        <Button type="submit" form="vehicle-form" disabled={isLoading}>
                            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Save & Next
                        </Button>
                    )}

                    {currentStep === 'bank' && (
                        <Button type="submit" form="banking-form" disabled={isLoading}>
                            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Save & Next
                        </Button>
                    )}
                    {currentStep === 'review' && (
                        <Button onClick={onComplete} disabled={isLoading} className="bg-success hover:bg-success/90 text-success-foreground">
                            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Complete Onboarding
                        </Button>
                    )}
                </CardFooter>
            </Card>
        </div>
    );
};

export default CreateDriverWizardPage;
